//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, {
  ComponentProps,
  ComponentPropsWithoutRef,
  ReactNode,
} from 'react';
import styled from 'styled-components';

import { PrimaryButton } from '@components/atoms/button/Button';
import { MultiRequiredPermissionsActionButton } from '@components/atoms/required-permissions-button/RequiredPermissionsButton';
import { BodySecondaryText } from '@components/atoms/text/Text';
import { ArrowButton } from '@uia/rights/UIARightsPreviousButton';

export interface UIAFooterProps {
  children: ReactNode;
  className?: string;
}

export const UIAFooter = ({ children, className }: UIAFooterProps) => (
  <Footer className={className}>
    <Container>{children}</Container>
  </Footer>
);

const Footer = styled.footer`
  display: flex;
`;

const Container = styled.div`
  width: 100%;
`;

const Actions = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: end;
    align-items: center;
    gap: ${theme.spacings[3]};
    padding: ${theme.spacings[3]};
    border-bottom: ${theme.borderWidths[1]} solid ${theme.colors.gray1};
    border-top: ${theme.borderWidths[1]} solid ${theme.colors.gray1};
  `,
);

const TopSection = styled.div(
  ({ theme }) => `
    display: flex;
    align-items: center;
    border-bottom: ${theme.borderWidths[1]} solid ${theme.colors.gray1};
    border-top: ${theme.borderWidths[1]} solid ${theme.colors.gray1};
  `,
);

const BottomSection = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: flex-end;
    margin: ${theme.spacings[5]} ${theme.spacings[3]};
  `,
);

interface PreviousButtonProps {
  onClick: () => void;
  trackingId: string;
}

const PreviousButton = ({ onClick, trackingId }: PreviousButtonProps) => (
  <ArrowButton
    iconName='chevron-left'
    onClick={onClick}
    trackingId={trackingId}
  >
    <PreviousButtonText>Previous</PreviousButtonText>
  </ArrowButton>
);

const PreviousButtonText = styled(BodySecondaryText)`
  margin-left: 0;
`;

// TODO: PD-269777 - when UIA React Port is released remove the border-radius
const PrimaryActionButton = styled(PrimaryButton)(
  ({ theme }) => `
    border-radius: ${theme.borderRadii[2]};
  `,
);

// TODO: PD-269777 - when UIA React Port is released remove the border-radius
const StyledMultiRequiredPermissionsActionButton = styled(
  MultiRequiredPermissionsActionButton,
)(
  ({ theme }) => `
    border-radius: ${theme.borderRadii[2]};
`,
);

const ActionButtonContent = styled.div(
  ({ theme }) => `
    display: flex;
    align-items: center;
    gap: ${theme.spacings[2]};
  `,
);

const ActionButton = ({
  children,
  ...otherProps
}: ComponentProps<typeof PrimaryActionButton>) => (
  <PrimaryActionButton {...otherProps}>
    <ActionButtonContent>{children}</ActionButtonContent>
  </PrimaryActionButton>
);

const RequiredPermissionsActionButton = ({
  children,
  ...otherProps
}: ComponentPropsWithoutRef<typeof MultiRequiredPermissionsActionButton>) => (
  <StyledMultiRequiredPermissionsActionButton {...otherProps}>
    <ActionButtonContent>{children}</ActionButtonContent>
  </StyledMultiRequiredPermissionsActionButton>
);

UIAFooter.Actions = Actions;
UIAFooter.ActionButton = ActionButton;
UIAFooter.BottomSection = BottomSection;
UIAFooter.RequiredPermissionsActionButton = RequiredPermissionsActionButton;
UIAFooter.PreviousButton = PreviousButton;
UIAFooter.TopSection = TopSection;
